/* global expenseFundApprovalsUpdateUrl uncommittedExpendituresPreCommitUrl */
import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Modal } from 'flowbite';

import UncommittedExpendituresEditModal from '../committed_expenditures/UncommittedExpendituresEditModal';

import { formatFigure } from 'Helpers/formatService';
import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import EditIcon from 'Assets/icons/edit.svg';

export default function PurchaseOrdersCreateCommittedExpendituresCreate(props) {
    const [uncommittedExpenditures, setUncommittedExpenditures] = useState([]);
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [updatedUncommittedExpenditure, setUpdatedUncommittedExpenditure] = useState({});
    const [uncommittedExpendituresLoading, setUncommittedExpendituresLoading] = useState(true);
    const [suppliersLoading, setSuppliersLoading] = useState(true);
    const [isRequesting, setIsRequesting] = useState(1);
    const [isRequested, setIsRequested] = useState(false);
    const [uncommittedExpendituresEditModalElement, setUncommittedExpendituresEditModalElement] = useState(null);

    const uncommittedExpendituresEditModalRef = useRef();
    // Guard async state writes against unmount (a second in-flight fetch can resolve after the
    // step unmounts on navigation).
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        fetchUncommittedExpenditures();
        fetchSuppliers();
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        uncommittedExpendituresEditModalRef &&
            uncommittedExpendituresEditModalRef.current &&
            setUncommittedExpendituresEditModalElement(new Modal(uncommittedExpendituresEditModalRef.current, modalOptions(false)));
    }, [uncommittedExpendituresEditModalRef, uncommittedExpendituresEditModalRef.current]);

    useEffect(() => {
        fetchUncommittedExpenditures();
    }, [props.uncommittedExpenditureId]);

    useEffect(() => {
        if (Object.keys(updatedUncommittedExpenditure).length > 0) {
            uncommittedExpendituresEditModalElement && uncommittedExpendituresEditModalElement.show();
        }
    }, [updatedUncommittedExpenditure]);

    function clickRow(uncommittedExpenditure) {
        if (uncommittedExpenditure.expense.type === 'sport') {
            return;
        }

        if (uncommittedExpenditure.expense.type === 'project') {
            props.setExpId(uncommittedExpenditure.id);
        } else {
            setUpdatedUncommittedExpenditure(uncommittedExpenditure);
        }
    }

    const successHandler = () => {
        uncommittedExpendituresEditModalElement && uncommittedExpendituresEditModalElement.hide();
        fetchUncommittedExpenditures();
        setUpdatedUncommittedExpenditure({});
    };

    const closeHandler = () => {
        uncommittedExpendituresEditModalElement && uncommittedExpendituresEditModalElement.hide();
        setUpdatedUncommittedExpenditure({});
    };

    const fetchUncommittedExpenditures = () => {
        setUncommittedExpendituresLoading(true);

        let url = props.committedExpendituresCreateUrl;

        if (url.includes('UNCOMMITTED_EXPENDITURE_GROUP_ID')) {
            url = url.replace('UNCOMMITTED_EXPENDITURE_GROUP_ID', props.uncommittedExpenditureGroupId);
        } else {
            url += '?uncommittedExpenditureGroupId=' + props.uncommittedExpenditureGroupId;
        }

        if (props.uncommittedExpenditureId) {
            url += '&uncommitted_expenditure_id=' + props.uncommittedExpenditureId;
        }

        axios
            .get(url)
            .then((res) => {
                // A concurrent fetch can resolve after the auto-advance unmounts this step.
                if (!isMountedRef.current) {
                    return;
                }
                setUncommittedExpenditures(res.data);
                setUncommittedExpendituresLoading(false);
            })
            .catch((error) => {
                if (!isMountedRef.current) {
                    return;
                }
                setUncommittedExpenditures([]);
                setUncommittedExpendituresLoading(false);
            });
    };

    const fetchSuppliers = () => {
        setSuppliersLoading(true);
        axios
            .get(props.suppliersIndexUrl + '?without_pagination=1')
            .then((res) => {
                if (!isMountedRef.current) {
                    return;
                }
                setSupplierOptions(
                    res.data.map((supplier) => {
                        return { value: supplier.id, label: supplier.name };
                    }),
                );
                setSuppliersLoading(false);
            })
            .catch((error) => {
                if (!isMountedRef.current) {
                    return;
                }
                setSupplierOptions([]);
                setSuppliersLoading(false);
            });
    };

    // Apply the (budgeted) lines and advance to the final detailed-edit step. Shared by the manual
    // "Apply" button and the resume auto-advance, so both take the identical path (IB-610).
    const applyCommittedExpenditures = useCallback(
        (lines) => {
            props.setLineItems(lines);
            const maxAmountObjectLength = lines.reduce((globalMax, item) => {
                // start with the top-level amountObject length
                let itemMax = item.amountObject?.length || 0;

                // then check each nested uncommitted_expenditure’s amountObject
                if (Array.isArray(item.uncommitted_expenditures)) {
                    for (const ue of item.uncommitted_expenditures) {
                        const len = ue.amountObject?.length || 0;
                        if (len > itemMax) itemMax = len;
                    }
                }

                // bump up the global max if this item (or one of its UEs) was bigger
                return itemMax > globalMax ? itemMax : globalMax;
            }, 0);
            props.setMaxQuantities(maxAmountObjectLength);
            props.nextStep(3);
            props.setExpId(null);
        },
        [props],
    );

    const storeCommittedExpenditures = (isBudgetted) => {
        if (isBudgetted) {
            applyCommittedExpenditures(uncommittedExpenditures);
            return;
        }

        setIsRequesting(true);

        axios
            .post(props.committedExpendituresStoreUrl, {
                uncommittedExpenditureGroupId: props.uncommittedExpenditureGroupId,
                isBudgetted: isBudgetted,
            })
            .then((res) => {
                if (res.data.status === `ok`) {
                    if (isBudgetted) {
                        window.location.replace(props.committedExpendituresIndexUrl);
                    } else {
                        props.setCommittedExpenditures([]);
                        props.setUncommittedExpenditureGroup(null);
                        props.nextStep(1);
                    }
                }
                if (res.data.status === `requested`) {
                    setIsRequested(true);
                }
                setIsRequesting(false);
            })
            .catch((error) => {
                setIsRequesting(false);
                if (!isBudgetted) {
                    props.setUncommittedExpenditureGroup(null);
                    props.setCommittedExpenditures([]);
                    props.nextStep(1);
                }
            });
    };

    const isProjectSblExpense = (uncommittedExpenditure) => {
        return uncommittedExpenditure.expense.project_id !== null && uncommittedExpenditure.expense.type === 'expense';
    };

    if (uncommittedExpenditures.length === 0) {
        return null;
    }

    return (
        <>
            {uncommittedExpendituresLoading || suppliersLoading ? (
                <LoadingSpinner />
            ) : (
                <div>
                    <div className="table__card tw-mt-4">
                        <div>
                            <h5 className="table__header">Proceed to Commit Item/s</h5>
                        </div>
                        <div className="inbud-table_wrapper tw-mt-6">
                            <table className="inbud-table">
                                <thead>
                                    <tr>
                                        <th className="tw-w-2/12 tw-text-left">{$t('Name')}</th>
                                        <th className="tw-w-1/12">{$t('Cash')}</th>
                                        {props.vikEnabled && <th className="tw-w-1/12">{$t('VIK')}</th>}

                                        <th className="tw-w-1/12">{$t('Total')}</th>
                                        <th className="tw-w-1/12">{$t('Actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {uncommittedExpenditures.map((uncommittedExpenditure) => {
                                        const rows = [];
                                        // Add parent row
                                        rows.push(
                                            <tr key={uncommittedExpenditure.id}>
                                                <td>{uncommittedExpenditure.expense.name}</td>
                                                <td className="inbud-numeric-cell">{formatFigure(uncommittedExpenditure.cashflow_object.cash)}</td>
                                                {props.vikEnabled && (
                                                    <td className="inbud-numeric-cell">{formatFigure(uncommittedExpenditure.cashflow_object.vik)}</td>
                                                )}

                                                <td className="inbud-numeric-cell">{formatFigure(uncommittedExpenditure.cashflow_object.total)}</td>
                                                <td>
                                                    {uncommittedExpenditure.edit_allowed && (
                                                        <div
                                                            className="tw-flex tw-flex-row tw-items-center tw-gap-2 tw-cursor-pointer"
                                                            onClick={() => clickRow(uncommittedExpenditure)}
                                                        >
                                                            <EditIcon className="tw-w-3 tw-h-3" />
                                                            <p className="font-italic text-secondary">{$t('Click row to Edit')}</p>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>,
                                        );

                                        // Add child rows if parent is project type
                                        if (uncommittedExpenditure.expense.type === 'project' && uncommittedExpenditure.uncommitted_expenditures) {
                                            uncommittedExpenditure.uncommitted_expenditures.forEach((childExpenditure) => {
                                                rows.push(
                                                    <tr key={childExpenditure.id} className="tw-bg-gray-50">
                                                        <td className="tw-pl-8">{childExpenditure.expense.name}</td>
                                                        <td className="inbud-numeric-cell">{formatFigure(childExpenditure.cashflow_object.cash)}</td>
                                                        {props.vikEnabled && (
                                                            <td className="inbud-numeric-cell">{formatFigure(childExpenditure.cashflow_object.vik)}</td>
                                                        )}

                                                        <td className="inbud-numeric-cell">{formatFigure(childExpenditure.cashflow_object.total)}</td>
                                                        <td>
                                                            {childExpenditure.edit_allowed && (
                                                                <div
                                                                    className="tw-flex tw-flex-row tw-items-center tw-gap-2 tw-cursor-pointer"
                                                                    onClick={() => clickRow(childExpenditure)}
                                                                >
                                                                    <EditIcon className="tw-w-3 tw-h-3" />
                                                                    <p className="font-italic text-secondary">{$t('Click row to Edit')}</p>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>,
                                                );
                                            });
                                        }

                                        return rows;
                                    })}
                                    {uncommittedExpenditures.length === 0 && (
                                        <tr>
                                            <td colSpan={props.vikEnabled ? 5 : 4}>{$t('There are no uncommitted expenditures')}.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="p-3 float-right">
                        {!isRequested && (
                            <Button variant="danger" onClick={() => storeCommittedExpenditures(false)}>
                                {$t('Cancel')}
                            </Button>
                        )}
                        {!isRequested && (
                            <Button type="button" className="button primary  ml-3" onClick={() => storeCommittedExpenditures(true)}>
                                {$t('Apply')}
                            </Button>
                        )}
                        {isRequested && (
                            <Button type="button" className="button primary ml-3" disabled>
                                {$t('Commit Expenditures Requested')}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            <UncommittedExpendituresEditModal
                modalId="edit-uncommitted-expenditure"
                modalRef={uncommittedExpendituresEditModalRef}
                modalElement={uncommittedExpendituresEditModalElement}
                uncommittedExpenditure={updatedUncommittedExpenditure}
                uncommittedExpendituresUpdateUrl={props.uncommittedExpendituresUpdateUrl}
                expenseFundApprovalsUpdateUrl={expenseFundApprovalsUpdateUrl}
                uncommittedExpendituresPreCommitUrl={uncommittedExpendituresPreCommitUrl}
                closeHandler={closeHandler}
                updateSuccessHandler={successHandler}
                isProjectSblExpense={() => isProjectSblExpense(updatedUncommittedExpenditure)}
            />
        </>
    );
}
